// Pure helpers voor de Onboarding-module (F2 + F3.2/F3.3 templates).
//
// Géén DB-calls, géén Fastify-dependencies — alleen UTC-veilige rekenfuncties
// over enums + datums die zowel in de API als in de backoffice-UI gebruikt
// kunnen worden.

// batch-42 onboarding (FO-369) — de blokkade-reden-union woont in @streep/types
// (de laag onder dit pakket) zodat het weergave-DTO en deze resolver dezelfde
// bron delen. Alleen een type-import, dus geen runtime-dependency.
import { ONBOARDING_EIND_STATUSSEN, type OnboardingBlokkadeReden } from '@streep/types'

// ────────────────────────────────────────────────────────────────────────────
// Types — losgekoppeld van Prisma zodat dit pakket geen Prisma-dep heeft.
// ────────────────────────────────────────────────────────────────────────────

export type OnboardingStapTypeValue =
  | 'PERSOONLIJK'
  | 'CONTRACT'
  | 'CERTIFICATEN'
  | 'DOCUMENTEN'
  | 'IT_TOEGANG'
  | 'WERKKLEDING'
  | 'AKKOORD'

export type OnboardingStapStatusValue =
  | 'TODO'
  | 'BEZIG'
  // Spec 25 §4 — kandidaat heeft ingediend; wacht op HR-controle.
  // Was voorheen impliciet (= `BEZIG`), wat de wizard-cursor 'm als "huidig"
  // liet behouden. Cursor skipt INGEDIEND nu; HR-controle zet 'm naar
  // AFGEROND of AFGEWEZEN.
  | 'INGEDIEND'
  | 'AFGEROND'
  | 'OVERGESLAGEN'
  // F3.3 — HR keurde de stap af; medewerker moet opnieuw indienen.
  | 'AFGEWEZEN'

export type OnboardingStatusValue =
  | 'CONCEPT'
  | 'WACHT_OP_MEDEWERKER'
  | 'IN_BEHANDELING'
  | 'IN_CONTROLE'
  | 'WACHT_OP_HANDTEKENING'
  | 'ASSETS_TOEWIJZEN'
  | 'GEREED_VOOR_START'
  | 'ACTIEF'
  | 'AFGEROND'
  | 'GEANNULEERD'

// F3.2 — categorieën uit spec §3.3-tabel (1:1 met Prisma-enum).
export type OnboardingCategorieValue =
  | 'PERSOONLIJK'
  | 'COMPLIANCE'
  | 'VAKBEKWAAMHEID'
  | 'JURIDISCH'
  | 'UITRUSTING'
  | 'MOBILITEIT'
  | 'SYSTEMEN'
  | 'VEILIGHEID'
  | 'KEUZE'
  // batch-39 onboarding (FO-337) — samengevoegde stap "Reglementen & akkoorden".
  | 'REGLEMENTEN'

export type WieVultStapValue = 'HR' | 'MEDEWERKER'

/**
 * Subset van een `OnboardingTemplate` voor `generateStappenFromTemplate()`.
 * Bewust losgekoppeld van Prisma-types zodat dit pakket geen Prisma-dep heeft.
 */
export interface TemplateStapInput {
  id?: string
  volgnummer: number
  categorie: OnboardingCategorieValue
  titel: string
  beschrijving?: string | null
  formulierSchema: unknown
  wieVult?: WieVultStapValue
}

export interface TemplateInput {
  /** Optioneel — wordt op de aangemaakte stap-rij gezet als `templateStapId`. */
  id?: string
  stappen: TemplateStapInput[]
}

/**
 * Mapping van categorie → klassieke stap-type uit v1. Zonder mapping kunnen
 * we de bestaande `OnboardingStapType`-kolom (NOT NULL) niet vullen wanneer
 * we een template-gebaseerde stap genereren. Eén categorie wint per type
 * volgens spec §3.3-stap-verdeling.
 *
 * Cave: dit is een **bridge-mapping** voor de coexistentie-fase. Wanneer
 * F3.4+ klaar is en de v1-`OnboardingStapType`-enum kan vervallen, mag deze
 * mapping verdwijnen.
 */
const CATEGORIE_TO_STAP_TYPE: Record<OnboardingCategorieValue, OnboardingStapTypeValue> = {
  PERSOONLIJK:    'PERSOONLIJK',
  COMPLIANCE:     'DOCUMENTEN',
  VAKBEKWAAMHEID: 'CERTIFICATEN',
  JURIDISCH:      'CONTRACT',
  UITRUSTING:     'WERKKLEDING',
  MOBILITEIT:     'WERKKLEDING', // Geen eigen v1-type; valt samen met uitrusting.
  SYSTEMEN:       'IT_TOEGANG',
  VEILIGHEID:     'AKKOORD',
  KEUZE:          'WERKKLEDING', // Spec §15.1 — standaard KEUZE-stap is "Voorkeuren kleding & uitrusting".
  // batch-39 onboarding (FO-337) — de samengevoegde reglementen-stap is de
  // opvolger van de VEILIGHEID-akkoordstap → zelfde v1-bridge-type.
  REGLEMENTEN:    'AKKOORD',
}

/**
 * Volgorde 1..7 — leidend bij het seeden van de stap-rijen en bij UI-sortering.
 */
export const ONBOARDING_STAP_VOLGORDE_LIST: ReadonlyArray<OnboardingStapTypeValue> = [
  'PERSOONLIJK',
  'CONTRACT',
  'CERTIFICATEN',
  'DOCUMENTEN',
  'IT_TOEGANG',
  'WERKKLEDING',
  'AKKOORD',
]

// ────────────────────────────────────────────────────────────────────────────
// Datum-helper
// ────────────────────────────────────────────────────────────────────────────

const MS_PER_DAY = 1000 * 60 * 60 * 24

function toUtcMidnight(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

// ────────────────────────────────────────────────────────────────────────────
// Pure rekenfuncties
// ────────────────────────────────────────────────────────────────────────────

/**
 * Output-shape voor `generateStappenFromTemplate()` / `generateDefaultStappen()`.
 *
 * `categorie`/`templateStapId` zijn alleen aanwezig wanneer er een template is
 * gebruikt — bij `generateDefaultStappen()` (legacy hardcoded set) blijven ze
 * `undefined`.
 */
export interface GeneratedStapPayload {
  type: OnboardingStapTypeValue
  status: OnboardingStapStatusValue
  volgorde: number
  categorie?: OnboardingCategorieValue
  templateStapId?: string
}

/**
 * Genereer de standaard 7-staps payload bij het aanmaken van een Onboarding.
 * Alle stappen krijgen status TODO en volgorde 1..7.
 *
 * Returnt een nieuwe array zodat callers veilig kunnen muteren.
 *
 * F3.2 — Voor nieuwe code is `generateStappenFromTemplate()` te prefereren.
 * Deze functie blijft bestaan als achterwaarts-compatibele fallback voor
 * code-paden waar nog geen template is geseed of geselecteerd.
 */
export function generateDefaultStappen(): GeneratedStapPayload[] {
  return ONBOARDING_STAP_VOLGORDE_LIST.map((type, idx) => ({
    type,
    status: 'TODO' as OnboardingStapStatusValue,
    volgorde: idx + 1,
  }))
}

/**
 * F3.2 — Genereer onboarding-stappen vanuit een template. Volgorde wordt
 * bepaald door `volgnummer` op de template-stappen; de output-array is
 * gesorteerd op `volgorde` ascending. Elk template-stap-record gaat 1:1 mee
 * naar een nieuw `OnboardingStap`-record.
 *
 * - `type` wordt afgeleid uit de `categorie` via `CATEGORIE_TO_STAP_TYPE`
 *   (bridge-mapping voor de coexistentie-fase met v1-flow).
 * - `templateStapId` wordt overgenomen wanneer de input dat heeft; bij
 *   handmatige in-memory templates (zoals in unit-tests) kan deze leeg blijven.
 *
 * Throws alleen bij dubbele volgnummers (data-corruptie) of een lege lijst.
 *
 * Meerdere stappen mogen op hetzelfde afgeleide `type` mappen — dat is by
 * design: UITRUSTING, MOBILITEIT en KEUZE collapsen alle drie naar het
 * legacy v1-type WERKKLEDING. De vroegere unique([onboardingId, type])
 * DB-constraint is daarom verwijderd (migration
 * 20260606000000_onboarding_stap_drop_type_unique); de stap-identiteit is
 * `id`/`volgorde`, en `categorie` onderscheidt de stappen in de UI.
 */
export function generateStappenFromTemplate(template: TemplateInput): GeneratedStapPayload[] {
  if (template.stappen.length === 0) {
    throw new Error('Template heeft geen stappen; kan geen onboarding aanmaken')
  }

  const seenVolgnummers = new Set<number>()
  const out: GeneratedStapPayload[] = []

  const sorted = [...template.stappen].sort((a, b) => a.volgnummer - b.volgnummer)

  for (let i = 0; i < sorted.length; i++) {
    const s = sorted[i]
    if (seenVolgnummers.has(s.volgnummer)) {
      throw new Error(`Template heeft duplicate volgnummer ${s.volgnummer}`)
    }
    seenVolgnummers.add(s.volgnummer)

    const stapType = CATEGORIE_TO_STAP_TYPE[s.categorie]

    out.push({
      type:           stapType,
      status:         'TODO',
      volgorde:       i + 1,
      categorie:      s.categorie,
      templateStapId: s.id,
    })
  }

  return out
}

/**
 * Tel hoeveel stappen status AFGEROND of OVERGESLAGEN hebben — d.w.z. "klaar".
 * Wordt gebruikt voor de progress-indicator en de "kan ik voltooien?"-check.
 *
 * AFGEWEZEN telt **niet** als klaar — een afgewezen stap blokkeert de
 * onboarding-voltooiing tot HR/medewerker hem opnieuw indient (F3.3).
 */
export function aantalStappenKlaar(
  stappen: Array<{ status: OnboardingStapStatusValue }>,
): number {
  return stappen.filter((s) => s.status === 'AFGEROND' || s.status === 'OVERGESLAGEN').length
}

/**
 * F3.2 — voortgang als fractie 0..1 voor lijst-views en dashboard. Gemiddeld
 * gerond op 2 decimalen. Lege lijst → 0.
 */
export function berekenVoortgang(
  stappen: Array<{ status: OnboardingStapStatusValue }>,
): number {
  if (stappen.length === 0) return 0
  const klaar = aantalStappenKlaar(stappen)
  return Math.round((klaar / stappen.length) * 100) / 100
}

/**
 * True wanneer ALLE stappen klaar zijn (AFGEROND of OVERGESLAGEN). Geeft
 * `false` bij een lege lijst — een onboarding zonder stappen kan nooit voltooid
 * zijn.
 */
export function isOnboardingVoltooibaar(
  stappen: Array<{ status: OnboardingStapStatusValue }>,
): boolean {
  if (stappen.length === 0) return false
  return stappen.every((s) => s.status === 'AFGEROND' || s.status === 'OVERGESLAGEN')
}

/**
 * Doorlooptijd in dagen tussen ingediendOp en voltooidOp. Returnt null als de
 * onboarding nog niet voltooid is, of als voltooidOp vóór ingediendOp ligt
 * (data-corruptie — defensief 0 zou misleiden).
 */
export function doorlooptijdDagen(
  ingediendOp: Date,
  voltooidOp: Date | null | undefined,
): number | null {
  if (!voltooidOp) return null
  const start = toUtcMidnight(ingediendOp)
  const end = toUtcMidnight(voltooidOp)
  if (end < start) return null
  return Math.round((end - start) / MS_PER_DAY)
}

/**
 * Gemiddelde doorlooptijd over een lijst van afgeronde onboardings. Returnt
 * null als er geen valide samples zijn (lege lijst of allemaal null).
 *
 * Afronding op 1 decimaal voor UI-leesbaarheid.
 */
export function gemiddeldeDoorlooptijd(
  paren: Array<{ ingediendOp: Date; voltooidOp: Date | null | undefined }>,
): number | null {
  const samples: number[] = []
  for (const p of paren) {
    const d = doorlooptijdDagen(p.ingediendOp, p.voltooidOp)
    if (d !== null) samples.push(d)
  }
  if (samples.length === 0) return null
  const sum = samples.reduce((a, b) => a + b, 0)
  return Math.round((sum / samples.length) * 10) / 10
}

/**
 * Toegestane status-overgangen voor de hoofd-onboarding-status. Mismatched
 * input → false. Gebruikt door de PATCH-route en bij UI-knoppen.
 *
 * Spec C §3.2 — uitgebreide lifecycle voor de C-flow. Spec §18.7 ("additief —
 * bestaande code mag niet breken") betekent dat de legacy CONCEPT →
 * IN_BEHANDELING jump én IN_BEHANDELING → AFGEROND blijven werken naast de
 * nieuwe pijplijn. Dat is niet hetzelfde als ze náást elkaar te kunnen
 * gebruiken — een onboarding loopt óf via de legacy 4-staps-flow óf via de
 * C-pijplijn. De helper kent beide paden zodat oude en nieuwe onboardings
 * tegelijk in de database kunnen blijven.
 *
 * C-flow overgangen:
 *   CONCEPT               → WACHT_OP_MEDEWERKER | IN_BEHANDELING (legacy) | GEANNULEERD
 *   WACHT_OP_MEDEWERKER   → IN_BEHANDELING | GEANNULEERD
 *   IN_BEHANDELING        → IN_CONTROLE | AFGEROND (legacy) | GEANNULEERD
 *   IN_CONTROLE           → WACHT_OP_HANDTEKENING | IN_BEHANDELING (ronde-1 afkeur) | GEANNULEERD
 *   WACHT_OP_HANDTEKENING → ASSETS_TOEWIJZEN | GEANNULEERD
 *   ASSETS_TOEWIJZEN      → GEREED_VOOR_START | IN_CONTROLE (ronde-2 afkeur) | GEANNULEERD
 *   GEREED_VOOR_START     → ACTIEF | GEANNULEERD
 *   ACTIEF                → (eindstatus C-flow)
 *   AFGEROND              → (eindstatus legacy)
 *   GEANNULEERD           → (eindstatus, vanuit ELKE status behalve ACTIEF/AFGEROND)
 *
 * Cancel-regel: GEANNULEERD is bereikbaar vanaf elke status behalve de
 * twee eindstatussen ACTIEF en AFGEROND (spec §3.2 — een afgeronde
 * onboarding kun je niet meer annuleren; opheffen gaat via de
 * employee-status-flow).
 */
export function isStatusOvergangToegestaan(
  van: OnboardingStatusValue,
  naar: OnboardingStatusValue,
): boolean {
  if (van === naar) return false

  // Eindstatussen — geen verdere overgangen toegestaan.
  if (van === 'ACTIEF' || van === 'AFGEROND' || van === 'GEANNULEERD') {
    return false
  }

  // Universele cancel-pad: vanuit ELKE non-eindstatus mag je naar GEANNULEERD.
  if (naar === 'GEANNULEERD') return true

  switch (van) {
    case 'CONCEPT':
      // Nieuwe C-flow: → WACHT_OP_MEDEWERKER.
      // Legacy 4-staps flow: → IN_BEHANDELING (auto-promote bij eerste stap).
      return naar === 'WACHT_OP_MEDEWERKER' || naar === 'IN_BEHANDELING'
    case 'WACHT_OP_MEDEWERKER':
      return naar === 'IN_BEHANDELING'
    case 'IN_BEHANDELING':
      // C-flow: → IN_CONTROLE (4-ogencontrole ronde 1).
      // Legacy: → AFGEROND (alles klaar in oude 4-staps flow).
      return naar === 'IN_CONTROLE' || naar === 'AFGEROND'
    case 'IN_CONTROLE':
      // Goedkeur ronde 1: → WACHT_OP_HANDTEKENING.
      // Afkeur ronde 1: → IN_BEHANDELING (terug naar medewerker-stappen).
      return naar === 'WACHT_OP_HANDTEKENING' || naar === 'IN_BEHANDELING'
    case 'WACHT_OP_HANDTEKENING':
      return naar === 'ASSETS_TOEWIJZEN'
    case 'ASSETS_TOEWIJZEN':
      // Goedkeur ronde 2: → GEREED_VOOR_START.
      // Afkeur ronde 2: → IN_CONTROLE (terug naar ronde-1 reviewers).
      return naar === 'GEREED_VOOR_START' || naar === 'IN_CONTROLE'
    case 'GEREED_VOOR_START':
      return naar === 'ACTIEF'
    default:
      return false
  }
}

/**
 * batch-45 onboarding (FO-381 §3.2) — mag "Afronden buiten de flow om" vanuit
 * deze status?
 *
 * Geforceerd afronden is een bewuste bypass van de tussenstations: resterende
 * stappen worden OVERGESLAGEN en de onboarding springt naar GEREED_VOOR_START,
 * waarna het bestaande, bewust handmatige activeer-moment
 * (GEREED_VOOR_START -> ACTIEF) de afronding blijft. Daarom is dit een eigen
 * helper en GEEN verruiming van `isStatusOvergangToegestaan`: de reguliere
 * state-machine blijft exact zoals hij was (geen tweede afrond-waarheid, T10).
 *
 * Toegestaan vanuit elke niet-eindstatus behalve GEREED_VOOR_START zelf: daar
 * is niets meer te forceren (de activeer-knop staat al klaar; losse stappen
 * overslaan kan via de bestaande stap-skip).
 *
 * FO-414 §3.5 — heroverwogen en bewust zo gelaten: GEREED_VOOR_START is geen
 * status zonder uitweg (de reguliere overgang is "Activeren"), en een tweede
 * afrond-knop naast Activeren zou juist twee waarheden over "afgerond" maken.
 * Wél is geforceerd afronden sinds FO-414 in één actie tot ACTIEF door te
 * zetten (`directActiveren` op de voltooi-route) - dat loopt via dezelfde
 * activatie-bron als de knop, niet via een verruiming van deze helper.
 */
export function isGeforceerdAfrondenToegestaan(status: OnboardingStatusValue): boolean {
  return (
    status !== 'ACTIEF' &&
    status !== 'AFGEROND' &&
    status !== 'GEANNULEERD' &&
    status !== 'GEREED_VOOR_START'
  )
}

/**
 * Toegestane stap-status-overgangen.
 *
 * Regels (Spec 25 §4 — INGEDIEND toegevoegd):
 *   TODO          → BEZIG | INGEDIEND | AFGEROND | OVERGESLAGEN | AFGEWEZEN
 *   BEZIG         → INGEDIEND | AFGEROND | OVERGESLAGEN | AFGEWEZEN | TODO
 *   INGEDIEND     → AFGEROND | OVERGESLAGEN | AFGEWEZEN | BEZIG | TODO
 *                   (HR keurt goed/af; kandidaat mag herzien vóór HR-controle)
 *   AFGEROND      → TODO                            (heropenen door HR)
 *   OVERGESLAGEN  → TODO                            (heropenen door HR)
 *   AFGEWEZEN     → TODO | BEZIG | INGEDIEND        (medewerker herziet + dient opnieuw in)
 */
export function isStapStatusOvergangToegestaan(
  van: OnboardingStapStatusValue,
  naar: OnboardingStapStatusValue,
): boolean {
  if (van === naar) return false
  if (van === 'TODO') return naar === 'BEZIG' || naar === 'INGEDIEND' || naar === 'AFGEROND' || naar === 'OVERGESLAGEN' || naar === 'AFGEWEZEN'
  if (van === 'BEZIG') return naar === 'INGEDIEND' || naar === 'AFGEROND' || naar === 'OVERGESLAGEN' || naar === 'AFGEWEZEN' || naar === 'TODO'
  if (van === 'INGEDIEND') return naar === 'AFGEROND' || naar === 'OVERGESLAGEN' || naar === 'AFGEWEZEN' || naar === 'BEZIG' || naar === 'TODO'
  if (van === 'AFGEROND') return naar === 'TODO'
  if (van === 'OVERGESLAGEN') return naar === 'TODO'
  if (van === 'AFGEWEZEN') return naar === 'TODO' || naar === 'BEZIG' || naar === 'INGEDIEND'
  return false
}

/**
 * Spec 25 §4 — Mag de medewerker deze stap (opnieuw) indienen?
 *
 * `isStapStatusOvergangToegestaan(status, 'AFGEROND')` is hiervoor niet
 * geschikt: AFGEWEZEN → AFGEROND is daar (terecht) false (HR doet die stap,
 * niet de medewerker), maar de medewerker MOET een afgewezen stap opnieuw
 * kunnen indienen. Tegelijk moet AFGEROND geblokkeerd blijven (lock).
 *
 * Toegestaan: TODO, BEZIG, INGEDIEND, AFGEWEZEN — INGEDIEND mag opnieuw worden
 * ingediend zolang de onboarding nog `IN_BEHANDELING`/`IN_CONTROLE` is
 * (kandidaat verbetert zichzelf vóór HR-controle).
 * Geblokkeerd: AFGEROND (HR moet eerst heropenen), OVERGESLAGEN (HR-besluit).
 */
export function kanStapIngediendWorden(status: OnboardingStapStatusValue): boolean {
  return status === 'TODO' || status === 'BEZIG' || status === 'INGEDIEND' || status === 'AFGEWEZEN'
}

/**
 * Spec 25 §4 — Bepaal de "huidige" stap voor de wizard-cursor: de eerste
 * stap met status TODO of AFGEWEZEN (vereist actie van de kandidaat).
 *
 * BEZIG en INGEDIEND tellen als "kandidaat is hier klaar/mee bezig, niet
 * blokkerend" — cursor skipt ze door. De kandidaat hervat een BEZIG-stap
 * via de stap-card op het dashboard, niet via auto-redirect.
 *
 * Returnt null als alles klaar is (alle stappen BEZIG/INGEDIEND/AFGEROND/
 * OVERGESLAGEN) → dashboard toont wacht-op-controle status.
 */
export function volgendeStap<T extends { status: OnboardingStapStatusValue; volgorde: number }>(
  stappen: T[],
): T | null {
  const sorted = [...stappen].sort((a, b) => a.volgorde - b.volgorde)
  const afgewezen = sorted.find((s) => s.status === 'AFGEWEZEN')
  if (afgewezen) return afgewezen
  const todo = sorted.find((s) => s.status === 'TODO')
  return todo ?? null
}

/**
 * Spec 25 §4 — Is de kandidaat klaar met zijn medewerker-stappen? Triggert
 * de overgang `IN_BEHANDELING → IN_CONTROLE` in de indienen-handler.
 *
 * Klaar = alle stappen `INGEDIEND` / `AFGEROND` / `OVERGESLAGEN`. `TODO`,
 * `BEZIG` of `AFGEWEZEN` ergens in de lijst betekent dat de kandidaat nog
 * actie moet ondernemen.
 *
 * Note: dit verschilt van `isOnboardingVoltooibaar` (= HR-klaar, alleen
 * AFGEROND/OVERGESLAGEN). De kandidaat-zijde is "klaar" zodra hij heeft
 * ingediend; HR moet nog goedkeuren.
 */
export function isMedewerkerKlaar(
  stappen: Array<{ status: OnboardingStapStatusValue }>,
): boolean {
  if (stappen.length === 0) return false
  return stappen.every(
    (s) => s.status === 'INGEDIEND' || s.status === 'AFGEROND' || s.status === 'OVERGESLAGEN',
  )
}

// ────────────────────────────────────────────────────────────────────────────
// batch-39 onboarding (FO-337) — status-éénbron + navigatie over de
// zichtbare actieve-stappen-lijst.
//
// Deze pure functies zijn dé bron voor wat een "actieve stap" is. De
// wizard-render (self.ts), de voortgang, de backoffice-weergave én de
// indienen-/promote-validatie gebruiken allemaal dit resultaat, zodat
// "ingediend, wacht op controle" aan beide kanten hetzelfde betekent
// (FO-337 §2.4).
// ────────────────────────────────────────────────────────────────────────────

/** Vereist de stap nog actie (van medewerker of HR)? */
export function isOpenStapStatus(status: OnboardingStapStatusValue): boolean {
  return status === 'TODO' || status === 'BEZIG' || status === 'AFGEWEZEN'
}

export interface EenbronStapInput {
  id: string
  volgorde: number
  status: OnboardingStapStatusValue
  categorie: OnboardingCategorieValue | null
  templateStap: { wieVult: WieVultStapValue } | null
}

export interface EenbronContext {
  /** Onboarding.templateId != null — legacy-onboardings (v1) zijn HR-beheerd. */
  isTemplateGedreven: boolean
  /**
   * Zijn er actieve reglementen voor déze medewerker (doelgroep-gefilterd)?
   * Zonder reglementen verbergt de REGLEMENTEN-stap zichzelf en telt hij
   * nergens als open (FO-337 §5).
   */
  heeftActieveReglementen: boolean
}

export interface EenbronResultaat<T> {
  /** Zichtbaar + invulbaar in de wizard, in volgorde. Dé lijst voor render/voortgang/navigatie. */
  medewerkerStappen: T[]
  /** HR-stappen (wieVult=HR, of legacy-onboarding): backoffice-werk, nooit in de wizard. */
  hrStappen: T[]
  /** Telt nergens mee: REGLEMENTEN-stap zonder reglementen, afgehandelde wees-stappen. */
  verborgenStappen: T[]
  /**
   * Configuratiefout: een OPEN stap in een template-gedreven onboarding
   * waarvan de template-stap verdwenen is (`templateStapId` op NULL geraakt,
   * bv. door de oude delete-then-create-seed). De wizard kan hem niet tonen,
   * dus de medewerker kan hem nooit afronden — dit blokkeert het indienen en
   * hoort een HR-signaal te geven (FO-337 §2.4).
   */
  nietInvulbareStappen: T[]
}

/**
 * Dé afleiding van de actieve stappen. Volgorde van het resultaat = de
 * zichtbare volgorde (gesorteerd op `volgorde`).
 */
export function bepaalActieveStappen<T extends EenbronStapInput>(
  stappen: T[],
  ctx: EenbronContext,
): EenbronResultaat<T> {
  const gesorteerd = [...stappen].sort((a, b) => a.volgorde - b.volgorde)
  const resultaat: EenbronResultaat<T> = {
    medewerkerStappen: [],
    hrStappen: [],
    verborgenStappen: [],
    nietInvulbareStappen: [],
  }

  for (const stap of gesorteerd) {
    if (stap.templateStap == null) {
      if (!ctx.isTemplateGedreven) {
        // Legacy v1-onboarding: alle stappen zijn HR-werk (geen wizard).
        resultaat.hrStappen.push(stap)
        continue
      }
      // Wees-stap in een template-gedreven onboarding. JURIDISCH hoort bij de
      // HR-contractflow (aparte teken-pagina) — geen configuratiefout.
      if (stap.categorie === 'JURIDISCH') {
        resultaat.hrStappen.push(stap)
        continue
      }
      if (isOpenStapStatus(stap.status)) {
        resultaat.nietInvulbareStappen.push(stap)
      } else {
        // Al afgehandeld (ingediend/afgerond/overgeslagen): geen actie meer nodig.
        resultaat.verborgenStappen.push(stap)
      }
      continue
    }
    if (stap.templateStap.wieVult === 'HR') {
      resultaat.hrStappen.push(stap)
      continue
    }
    if (stap.categorie === 'REGLEMENTEN' && !ctx.heeftActieveReglementen) {
      resultaat.verborgenStappen.push(stap)
      continue
    }
    resultaat.medewerkerStappen.push(stap)
  }
  return resultaat
}

/**
 * Is de medewerker-kant klaar voor HR-controle? Alle actieve medewerker-
 * stappen afgehandeld ÉN geen niet-invulbare (configuratiefout-)stappen —
 * anders zou "ingediend, wacht op controle" liegen (FO-337 §2.4).
 */
export function isKlaarVoorControle<T extends EenbronStapInput>(
  resultaat: EenbronResultaat<T>,
): boolean {
  return (
    resultaat.nietInvulbareStappen.length === 0 &&
    isMedewerkerKlaar(resultaat.medewerkerStappen)
  )
}

/**
 * FO-337 §2.3 — "Volgende" navigeert strikt vooruit over de zichtbare
 * geordende actieve-stappen-lijst: de eerstvolgende open stap NÁ de huidige
 * (nooit `nr ± 1`, nooit terugspringen — het gemelde 3 → 2 → 4-pad is
 * daarmee onmogelijk). Niets open ná de huidige → null (naar het dashboard,
 * dat eventuele eerdere open stappen toont).
 */
export function volgendeStapNrNaHuidige(
  stappen: Array<{ nr: number; status: string }>,
  huidigeNr: number,
): number | null {
  const idx = stappen.findIndex((s) => s.nr === huidigeNr)
  if (idx === -1) return null
  for (let i = idx + 1; i < stappen.length; i++) {
    const status = stappen[i].status
    if (status === 'TODO' || status === 'BEZIG' || status === 'AFGEWEZEN') {
      return stappen[i].nr
    }
  }
  return null
}

/**
 * FO-337 §2.3 — "Vorige" is bladeren: de direct voorgaande stap in de
 * zichtbare lijst (ongeacht status), of null op de eerste stap.
 */
export function vorigeStapNrVoorHuidige(
  stappen: Array<{ nr: number }>,
  huidigeNr: number,
): number | null {
  const idx = stappen.findIndex((s) => s.nr === huidigeNr)
  if (idx <= 0) return null
  return stappen[idx - 1].nr
}

// ────────────────────────────────────────────────────────────────────────────
// batch-42 onboarding (FO-369) — blokkade-resolver ("waarom staat deze
// onboarding open?").
//
// Eén pure functie die uit de bestaande data exact ÉÉN primaire blokkade
// afleidt, plus een sublijst met de overige actieve blokkades. Dé bron voor de
// lijst-kolom, de dashboard-telling-per-reden, de detail-banner en de na-
// startdatum-nudge — zodat "wacht op X" overal hetzelfde betekent. Geen DB, geen
// Prisma: direct unit-testbaar (spec §10.1).
// ────────────────────────────────────────────────────────────────────────────

export interface OnboardingBlokkadeInput {
  status: OnboardingStatusValue
  aanvullenVereist: boolean
  /**
   * Ontbrekende contract-veld-keys (leeg = compleet). Alleen betekenisvol op
   * IN_CONTROLE; de caller leidt ze af uit de Employee-contractvelden (zelfde
   * bron als `generateContractAndPromote`), of leest ze uit het persistente
   * `blokkadeDetails`-signaal.
   */
  ontbrekendeContractVelden?: readonly string[]
  /**
   * Dagen sinds de blokkerende toestand begon (bv. sinds de arbeidsovereenkomst
   * klaargezet is, voor "wacht op handtekening sinds N dagen"). null = onbekend.
   */
  sindsDagen?: number | null
  /**
   * FO-369 (review-fix) — zijn alle stappen goedgekeurd/overgeslagen? Alleen
   * betekenisvol op IN_CONTROLE: met alle stappen klaar + velden compleet is de
   * onboarding "klaar om het contract te genereren" (CONTRACT_GENEREREN) i.p.v.
   * "wacht op HR-controle" - dat onderscheid stuurt HR naar de juiste actie.
   */
  alleStappenKlaar?: boolean
}

export interface OnboardingBlokkade {
  /** De primaire (eerste in de keten) blokkade-reden. */
  reden: OnboardingBlokkadeReden
  /** true wanneer de onboarding ergens op wacht (reden !== 'GEEN'). */
  geblokkeerd: boolean
  /** Ontbrekende contract-veld-keys (alleen gevuld als CONTRACT_VELDEN_ONTBREKEN in de keten zit). */
  ontbrekendeVelden: string[]
  /** Dagen sinds de blokkerende toestand begon; null = onbekend/niet van toepassing. */
  sindsDagen: number | null
  /** Overige actieve blokkades (detail-sublijst). De primaire staat hier NIET in. */
  sublijst: OnboardingBlokkadeReden[]
}

// FO-414 — één bron voor de eindstatussen (types), niet een eigen kopie.
const BLOKKADE_EIND_STATUSSEN: readonly OnboardingStatusValue[] = ONBOARDING_EIND_STATUSSEN

/**
 * Leidt de blokkade af uit de onboarding-status + contractveld-staat +
 * aanvullen-vlag. Puur en totaal: elke status levert een deterministische reden.
 */
export function bepaalBlokkade(input: OnboardingBlokkadeInput): OnboardingBlokkade {
  const ontbrekendeVelden = [...(input.ontbrekendeContractVelden ?? [])]
  const sindsDagen = input.sindsDagen ?? null

  const geen: OnboardingBlokkade = {
    reden: 'GEEN',
    geblokkeerd: false,
    ontbrekendeVelden: [],
    sindsDagen: null,
    sublijst: [],
  }
  if (BLOKKADE_EIND_STATUSSEN.includes(input.status)) return geen

  // Geordende keten van actieve blokkades voor deze status.
  const keten: OnboardingBlokkadeReden[] = []
  switch (input.status) {
    case 'CONCEPT':
      keten.push('CONCEPT')
      break
    case 'WACHT_OP_MEDEWERKER':
    case 'IN_BEHANDELING':
      keten.push('WACHT_OP_MEDEWERKER')
      break
    case 'IN_CONTROLE':
      // §6.1 — velden ontbreken vóór aanvullen; zonder contract valt er niets te
      // tekenen. Zijn beide leeg: alle stappen goedgekeurd + velden compleet =
      // klaar om het contract te genereren (HR-actie), anders wacht op HR-controle.
      if (ontbrekendeVelden.length > 0) keten.push('CONTRACT_VELDEN_ONTBREKEN')
      if (input.aanvullenVereist) keten.push('AANVULLEN_VEREIST')
      if (keten.length === 0) {
        keten.push(input.alleStappenKlaar ? 'CONTRACT_GENEREREN' : 'WACHT_OP_CONTROLE')
      }
      break
    case 'WACHT_OP_HANDTEKENING':
      keten.push('WACHT_OP_HANDTEKENING')
      break
    case 'ASSETS_TOEWIJZEN':
      keten.push('ASSETS_TOEWIJZEN')
      break
    case 'GEREED_VOOR_START':
      keten.push('ACTIVEREN')
      break
    default:
      return geen
  }

  const [primair, ...rest] = keten
  return {
    reden: primair ?? 'GEEN',
    geblokkeerd: primair !== undefined && primair !== 'GEEN',
    ontbrekendeVelden: keten.includes('CONTRACT_VELDEN_ONTBREKEN') ? ontbrekendeVelden : [],
    sindsDagen,
    sublijst: rest,
  }
}
